import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';

import MagazineArticle from 'containers/MagazineArticle';
import JournalEntryForm from 'containers/JournalEntryForm';
import Button from 'components/Button';
import EmptyArticle from 'components/EmptyArticle';
import InfoBar from 'components/InfoBar';

import FeedInfiniteLoader from './FeedInfiniteLoader';
import FeedGoals from './FeedGoals';
import FeedQuestionnaire from './FeedQuestionnaire';
import FeedButtonItem from './FeedButtonItem';

import { FeedItemShape } from 'entities';
import * as actions from 'actions';
import { getFeedItems, getFeedPaging, getInfoState } from 'selectors';

import './style.scss';

class Feed extends Component {

  constructor() {
    super();

    this.checkRowLoaded = this.checkRowLoaded.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this.renderFeedInfiniteLoader = this.renderFeedInfiniteLoader.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleLoadMoreViaButton = this.handleLoadMoreViaButton.bind(this);
    this.handleInfoBarClose = this.handleInfoBarClose.bind(this);
  }

  componentDidMount() {
    if (!this.props.items.length && this.props.onLoadMore) {
      this.props.onLoadMore(this.props.page, false);
    }
  }

  handleInfoBarClose() {
    if(this.props.onInfoBarClose) {
      this.props.onInfoBarClose();
    }
  }

  handleLoadMoreViaButton(e) {
    e.preventDefault();
    this.props.onLoadMore(this.props.page, true);
  }

  handleLoadMore() {
    if (this.props.totalCount > this.props.items.length) {
      this.props.onLoadMore(this.props.page, this.props.hasFetchedManually);
    }
  }

  checkRowLoaded({ index }) {
    const hasNextPage = this.props.items.length <= this.props.totalCount;
    return hasNextPage && index < this.props.items.length;
  }

  renderRow({ index, key, style }) {
    return (
      <div
        key={key}
        style={style}
      >
        {this.checkRowLoaded({ index }) && this.renderItem(this.props.items[index])}
      </div>
    );
  }

  renderItem(item) {
    const { feedChat, feedInterests, feedJournal, feedQuestionnaire, feedGoals, magazineArticle } = item;

    if (feedChat) return this.renderFeedButton({item: feedChat, to: '/messages'});
    if (feedInterests) return this.renderFeedButton({item: feedInterests, to: '/interests'});
    if (feedJournal) return this.renderFeedJournal(feedJournal);
    if (feedQuestionnaire) return this.renderFeedQuestionnaire(feedQuestionnaire);
    if (feedGoals) return this.renderFeedGoals(feedGoals);
    if (magazineArticle) return this.renderMagazineArticle(magazineArticle);
  }

  renderFeedButton({item, to}) {
    return <FeedButtonItem {...item} to={to}/>;
  }

  renderFeedJournal(feedJournal) {
    return (
      <JournalEntryForm
        shouldRenderOnOneLine
        title={feedJournal.title}
        text={feedJournal.text}
        emojis={feedJournal.emojis}
      />
    );
  }

  renderFeedQuestionnaire(feedQuestionnaire) {
    return (
      <FeedQuestionnaire
        questionnaire={feedQuestionnaire}
      />
    );
  }

  renderFeedGoals(feedGoals) {
    return <FeedGoals title={feedGoals.title} goals={feedGoals.goals} />;
  }

  renderMagazineArticle(magazineArticle) {
    return (
      <MagazineArticle
        magazineArticle={magazineArticle}
        imageUrl={magazineArticle.thumbnail.main.lowRes}
        inFeed
        shouldOpenProfileFromImage
      />
    );
  }

  renderEmptyArticles() {
    return (
      <div>
        <EmptyArticle isDarker />
        <EmptyArticle isDarker />
        <EmptyArticle isDarker />
      </div>
    );
  }

  renderFeedInfiniteLoader() {
    const { items, page, totalCount, hasFetchedManually, isFetching } = this.props;

    // Use pass-thru props to trigger update & re-render on the FeedInfiniteLoader
    const passThruGoals = items.find(item => item.feedGoals);
    const passThruQuestionnaire = items.find(item => item.feedQuestionnaire);

    return (
      <FeedInfiniteLoader
        ref={ref => this._infiniteLoader = ref}
        page={page}
        totalCount={totalCount}
        hasFetchedManually={hasFetchedManually}
        isNextPageLoading={isFetching}
        isRowLoaded={this.checkRowLoaded}
        rowRenderer={this.renderRow}
        list={items}
        loadNextPage={this.handleLoadMore}
        passThruGoals={passThruGoals}
        passThruQuestionnaire={passThruQuestionnaire}
      />
    );
  }

  render() {
    const {
      items,
      isFetching,
      hasFetchedManually,
      isSuccessInfoBarVisible,
      successInfoBarTitle,
      successInfoBarMessage
    } = this.props;

    return (
      <section className="Feed content-wrapper has-background">
        <div className="wrapper-small is-collapsed is-padded-vertical">
          <div className="feed-layout">
            <div className="container">
              {isSuccessInfoBarVisible && (
                <InfoBar
                  onClose={this.handleInfoBarClose}
                  type="primary"
                  title={successInfoBarTitle}
                  message={successInfoBarMessage} />
              )}

              <div className="wrapper-small is-collapsed">
                {items.length === 0 && isFetching
                  ? this.renderEmptyArticles()
                  : this.renderFeedInfiniteLoader()
                }

                {hasFetchedManually ||
                    <Button
                      isYellow
                      iconType="preloader-dark"
                      hasIcon={isFetching}
                      isDisabled={isFetching}
                      onClick={this.handleLoadMoreViaButton}
                    >
                      {__('members.feed.loadMore')}
                    </Button>
                }

                {hasFetchedManually && isFetching && <EmptyArticle isDarker />}
              </div>
            </div>
          </div>

        </div>
      </section>
    );
  }
}

Feed.propTypes = {
  onLoadMore: PropTypes.func,
  items: PropTypes.arrayOf(FeedItemShape),
  isFetching: PropTypes.bool,
  page: PropTypes.number,
  totalCount: PropTypes.number,
  hasFetchedManually: PropTypes.bool,
  isSuccessInfoBarVisible: PropTypes.bool,
  successInfoBarMessage: PropTypes.string,
  successInfoBarTitle: PropTypes.string,
  onInfoBarClose: PropTypes.func
};

const mapStateToProps = state => {
  const successState = getInfoState(state, 'success');
  const isSuccessInfoBarVisible = successState.visible;
  const successInfoBarMessage = successState.message;
  const successInfoBarTitle = successState.title;

  return {
    items: getFeedItems(state),
    ...getFeedPaging(state),
    isSuccessInfoBarVisible,
    successInfoBarMessage,
    successInfoBarTitle
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onLoadMore: (page, isManualFetch) => {
      dispatch(actions.feedFetch(page + 1, isManualFetch));
    },
    onInfoBarClose: () => {
      dispatch(actions.infoClose('success'));
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Feed);



/** WEBPACK FOOTER **
 ** ./src/members/Main/Feed/index.js
 **/